import { Extension } from "@tiptap/react";

const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/;

/**
 * Relit une couleur déjà enregistrée.
 *
 * Le navigateur ne rend jamais `element.style.color` tel qu'il a été écrit : il
 * le sérialise en `rgb(r, g, b)`, y compris quand le HTML enregistré contient
 * `color: #c1121f`. Ne reconnaître que la notation hexadécimale revenait donc à
 * perdre la couleur à chaque relecture du champ — et à la réenregistrer effacée
 * à la modification suivante.
 */
export function parseStoredColor(raw: string | null | undefined): string | null {
  if (!raw) return null;

  const value = raw.trim();
  if (HEX_REGEX.test(value)) return value.toLowerCase();

  const rgb = value.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,[^)]*)?\)$/i,
  );
  if (!rgb) return null;

  const channels = rgb.slice(1, 4).map(Number);
  if (channels.some((c) => !Number.isInteger(c) || c < 0 || c > 255)) {
    return null;
  }

  return `#${channels.map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    cmsFontColor: {
      setFontColor: (color: string) => ReturnType;
      unsetFontColor: () => ReturnType;
    };
  }
}

export const CmsFontColor = Extension.create({
  name: "cmsFontColor",

  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          color: {
            default: null,
            parseHTML: (element) => parseStoredColor(element.style.color),
            renderHTML: (attributes) => {
              if (!attributes.color) return {};
              return { style: `color: ${attributes.color}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontColor:
        (color: string) =>
        ({ chain }) => {
          if (!HEX_REGEX.test(color)) return false;
          return chain().setMark("textStyle", { color }).run();
        },
      unsetFontColor:
        () =>
        ({ chain }) =>
          chain().setMark("textStyle", { color: null }).run(),
    };
  },
});
